import { parseColor, toHex, contrastRatio } from '../components/common/colorpicker/colorMath';

/* ─────────────────────────────────────────────────────────────
   Theme generator: nine seed colors in, the full canonical token
   set out. Pure functions, no DOM.

   The neutral ramp interpolates in OKLab (the rectangular form of
   OKLCH) so lightness steps look even and grays never pick up a
   stray hue between two neutral endpoints.
─────────────────────────────────────────────────────────────── */

export const TOKEN_NAMES = [
  '--color-primary', '--color-primary-dark', '--color-primary-light', '--color-primary-tint', '--color-primary-text',
  '--color-bg-page', '--color-bg-white',
  '--color-gray-50', '--color-gray-100', '--color-gray-200', '--color-gray-300', '--color-gray-400',
  '--color-gray-500', '--color-gray-600', '--color-gray-700', '--color-gray-800', '--color-gray-900',
  '--color-error', '--color-error-light', '--color-error-hover', '--color-error-text',
  '--color-success', '--color-success-light', '--color-success-text',
  '--color-warning', '--color-warning-light', '--color-warning-text',
  '--color-dirty-bg', '--color-dirty-border',
  '--color-bar-bg', '--color-bar-text', '--color-on-primary', '--color-on-error', '--color-on-success', '--color-on-warning',
  '--color-border', '--color-border-subtle',
  '--shadow-sm', '--shadow-md',
  '--border-width', '--focus-ring-width', '--radius-sm', '--icon-stroke',
];

// Labels name what the seed paints on screen, not the token it feeds: `surface` becomes
// --color-bg-white, which covers most of the app, so "Surface" alone read as an accent.
export const SEED_FIELDS = [
  { key: 'primary',  label: 'Primary',            hint: 'Primary buttons, links, active tab, selection' },
  { key: 'barBg',    label: 'Header and footer',  hint: 'Top header bar and bottom status bar' },
  { key: 'surface',  label: 'Panels',             hint: 'Sidebar, module panels, cards, grids and popups: most of the screen' },
  { key: 'pageBg',   label: 'Backdrop',           hint: 'The gutter behind and between the panels' },
  { key: 'text',     label: 'Text',               hint: 'Body text; the gray steps blend from Panels toward it' },
  { key: 'error',    label: 'Error',              hint: 'Delete buttons, failed states, error messages' },
  { key: 'success',  label: 'Success',            hint: 'Saved and passed states' },
  { key: 'warning',  label: 'Warning',            hint: 'Warnings; also edited (unsaved) fields' },
  { key: 'neutralTint', label: 'Gray tint color', hint: 'Hue mixed into the gray steps' },
];

/**
 * Generated tokens important enough to sit beside the seeds on the Colors tab. Each shows its
 * generated value and can be set by hand (Manual), the same as on the Tokens tab.
 */
export const DERIVED_FIELDS = [
  { token: '--color-primary-text', label: 'Accent text and icons', hint: 'Module titles, toolbar icons, links, active nav. Auto: Primary, shaded to read on Panels' },
  { token: '--color-bar-text', label: 'Header and footer text', hint: 'Text and icons on the header and footer bars. Auto: white or near-black, whichever reads' },
];

/** Plain-language name for each token: what it paints, in the builder's seed vocabulary. */
export const TOKEN_LABELS = {
  '--color-primary': 'Primary fill',
  '--color-primary-dark': 'Primary hover and pressed',
  '--color-primary-light': 'Selection and active background',
  '--color-primary-tint': 'Hover background',
  '--color-primary-text': 'Accent text and icons',
  '--color-bg-page': 'Backdrop',
  '--color-bg-white': 'Panels',
  '--color-gray-50': 'Subtle background (sections, headers)',
  '--color-gray-100': 'Row stripes, header rows, button fills',
  '--color-gray-200': 'Light dividers and fills',
  '--color-gray-300': 'Subtle lines; disabled text',
  '--color-gray-400': 'Decorative icons and chevrons',
  '--color-gray-500': 'Muted text',
  '--color-gray-600': 'Secondary text',
  '--color-gray-700': 'Nav, list and input text',
  '--color-gray-800': 'Labels',
  '--color-gray-900': 'Text',
  '--color-error': 'Error fill',
  '--color-error-light': 'Error tint',
  '--color-error-hover': 'Error hover',
  '--color-error-text': 'Error text',
  '--color-success': 'Success fill',
  '--color-success-light': 'Success tint',
  '--color-success-text': 'Success text',
  '--color-warning': 'Warning fill',
  '--color-warning-light': 'Warning tint',
  '--color-warning-text': 'Warning text',
  '--color-dirty-bg': 'Edited field background',
  '--color-dirty-border': 'Edited field border',
  '--color-bar-bg': 'Header and footer',
  '--color-bar-text': 'Header and footer text',
  '--color-on-primary': 'Text on primary fill',
  '--color-on-error': 'Text on error fill',
  '--color-on-success': 'Text on success fill',
  '--color-on-warning': 'Text on warning fill',
  '--color-border': 'Borders',
  '--color-border-subtle': 'Dividers and grid lines',
  '--shadow-sm': 'Small shadow',
  '--shadow-md': 'Large shadow',
  '--border-width': 'Line weight',
  '--focus-ring-width': 'Focus ring',
  '--radius-sm': 'Corner radius',
  '--icon-stroke': 'Icon stroke',
};

export const DEFAULT_SEEDS = {
  base: 'light',
  primary: '#1a5568',
  pageBg: '#f9fafb',
  surface: '#f3f4f6',
  text: '#111827',
  barBg: '#1a5568',
  error: '#dc2626',
  success: '#16a34a',
  warning: '#d97706',
  neutralMode: 'primary',
  borderWidth: '1px',
  focusRingWidth: '0px',
  radius: '4px',
  iconStroke: '2',
  overrides: [],
};

/* ── OKLab / OKLCH ─────────────────────────────────────────── */

const toLin = c => { const n = c / 255; return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4; };
const fromLin = c => {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * (Math.max(c, 0) ** (1 / 2.4)) - 0.055;
  return Math.min(255, Math.max(0, v * 255));
};

const rgbToOklab = ({ r, g, b }) => {
  const lr = toLin(r), lg = toLin(g), lb = toLin(b);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return {
    L: 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  };
};

const oklabToRgb = ({ L, a, b }) => {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.2914855480 * b) ** 3;
  return {
    r: fromLin(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: fromLin(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: fromLin(-0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s),
  };
};

const oklabToOklch = ({ L, a, b }) => ({ L, C: Math.hypot(a, b), h: Math.atan2(b, a) });
const oklchToOklab = ({ L, C, h }) => ({ L, a: C * Math.cos(h), b: C * Math.sin(h) });

/* ── Hex helpers ───────────────────────────────────────────── */

const FALLBACK = { r: 128, g: 128, b: 128, a: 1 };
const rgbOf = hex => parseColor(hex) || FALLBACK;
const hexOf = rgb => toHex(rgb);
const labOf = hex => rgbToOklab(rgbOf(hex));

/** Mix two hex colors in OKLab. t = 0 → a, t = 1 → b. */
export const mixHex = (a, b, t) => {
  const x = labOf(a), y = labOf(b);
  return hexOf(oklabToRgb({
    L: x.L + (y.L - x.L) * t,
    a: x.a + (y.a - x.a) * t,
    b: x.b + (y.b - x.b) * t,
  }));
};

/** Shift OKLCH lightness by delta (−1..1), clamped. */
const shiftLightness = (hex, delta) => {
  const c = oklabToOklch(labOf(hex));
  return hexOf(oklabToRgb(oklchToOklab({ ...c, L: Math.min(0.99, Math.max(0.05, c.L + delta)) })));
};

const ratioHex = (fg, bg) => contrastRatio(rgbOf(fg), rgbOf(bg));

/** White or near-black, whichever reads better on the given background. */
const readableOn = bg => (ratioHex('#ffffff', bg) >= ratioHex('#0a0a0a', bg) ? '#ffffff' : '#0a0a0a');

/* ── Generator ─────────────────────────────────────────────── */

const GRAY_STEPS = [200, 300, 400, 500, 600, 700, 800];

/*
 * Where each gray step sits between surface (0) and text (1), in OKLab. Not a
 * straight line: this is the average of the hand-tuned built-in themes, which keep
 * the light end bunched near the surface and bring the darks in fast. Floors below
 * then guarantee the two steps that carry meaning.
 */
const RAMP_CURVE = { 200: 0.064, 300: 0.133, 400: 0.286, 500: 0.473, 600: 0.640, 700: 0.811, 800: 0.921 };
// Dark bases: light-on-dark tiers separate far more visibly than dark-on-light ones, so the
// text steps (700 nav/list/inputs, 800 labels) sit close to the text color and read as one family.
const DARK_TEXT_CURVE = { 700: 0.90, 800: 0.96 };
const MUTED_MIN = 4.5;       // gray-500 (muted text) vs surface and vs page
const ICON_MIN = 3.0;        // gray-400 (decorative icons, chevrons) vs surface and vs page
const BORDER_MIN = 2.0;      // --color-border vs surface
const SUBTLE_MIN = 1.3;      // --color-border-subtle (dividers, grid lines) vs surface
const FILL_MIN = 1.06;       // gray-100 (zebra rows, header-row and button fills) vs surface
const DIVIDER_MIN = 1.12;    // gray-200 (light dividers) vs surface, always a step above gray-100
const NEUTRAL_CHROMA = 0.03; // tint strength at mid-ramp; fades toward both ends
const PRIMARY_LIGHT_MIN = 1.15;
const STATUS_LIGHT_MIN = 1.25;
const TEXT_MIN = 4.5;

/** Neutral ramp modes: hue from primary (default), pure gray, or the neutralTint seed. */
export const NEUTRAL_MODES = [
  { value: 'primary', label: 'From primary' },
  { value: 'gray', label: 'Neutral gray' },
  { value: 'tint', label: 'Custom tint' },
];

/** Token families, for the builder's per-family Auto / Custom toggle. */
export const TOKEN_FAMILIES = {
  neutral: ['--color-gray-100', '--color-gray-200', '--color-gray-300', '--color-gray-400', '--color-gray-500', '--color-gray-600',
    '--color-gray-700', '--color-gray-800', '--color-border', '--color-border-subtle'],
  primary: ['--color-primary-dark', '--color-primary-light', '--color-primary-tint', '--color-primary-text', '--color-on-primary', '--color-bar-text'],
  status: ['--color-error-light', '--color-error-hover', '--color-error-text', '--color-success-light', '--color-success-text',
    '--color-warning-light', '--color-warning-text', '--color-dirty-bg', '--color-on-error', '--color-on-success', '--color-on-warning'],
};

/**
 * Smallest step past t along surface→text that reaches the contrast target against the
 * surface and, when given, against the page too (text that sits on the page background).
 */
const pushTo = (surface, text, t, target, page = null, step = 0.01) => {
  let x = t;
  const ok = v => ratioHex(v, surface) >= target && (!page || ratioHex(v, page) >= target);
  while (x < 1 && !ok(mixHex(surface, text, x))) x += step;
  return Math.min(x, 1);
};

/** Keep a color's lightness, set its hue and chroma. */
const withHue = (hex, hue, chroma) => {
  const { L } = oklabToOklch(labOf(hex));
  return hexOf(oklabToRgb(oklchToOklab({ L, C: Math.max(chroma, 0), h: hue })));
};

/** Blend col toward the surface as far as possible (from start) while it still separates by min. */
const flooredBlend = (col, surface, start, min, stop = 0.3) => {
  for (let t = start; t >= stop; t -= 0.01) {
    const v = mixHex(col, surface, t);
    if (ratioHex(v, surface) >= min) return v;
  }
  return mixHex(col, surface, stop);
};

/** Shift a color's OKLCH lightness away from bg until it reaches the target contrast. */
const readableShade = (col, bg, target = TEXT_MIN) => {
  const up = oklabToOklch(labOf(bg)).L < 0.5;
  let v = col;
  for (let i = 0; i < 60 && ratioHex(v, bg) < target; i++) v = shiftLightness(v, up ? 0.02 : -0.02);
  return v;
};

/** White when it passes AA, otherwise near-black: keeps white wherever white already works. */
const onColor = bg => (ratioHex('#ffffff', bg) >= TEXT_MIN ? '#ffffff' : readableOn(bg));

/**
 * Full canonical token set from the seeds. Tokens named in seeds.overrides are
 * copied from previousTokens instead of recomputed (when previousTokens has them).
 */
export function generateTokens(seeds, previousTokens = {}) {
  const s = { ...DEFAULT_SEEDS, ...(seeds || {}) };
  const dark = String(s.base).toLowerCase() === 'dark';
  const page = s.pageBg, surface = s.surface, text = s.text;

  // Neutral ramp: eased curve, floors on muted text and borders, never stepping backwards.
  const curve = { ...RAMP_CURVE };
  const borderAt = pushTo(surface, text, curve[400], BORDER_MIN);
  curve[200] = pushTo(surface, text, curve[200], DIVIDER_MIN);
  curve[300] = pushTo(surface, text, curve[300], SUBTLE_MIN);
  curve[400] = pushTo(surface, text, borderAt, ICON_MIN, page);
  curve[500] = pushTo(surface, text, curve[500], MUTED_MIN, page);
  if (dark) Object.entries(DARK_TEXT_CURVE).forEach(([step, t]) => { curve[step] = Math.max(curve[step], t); });
  let prev = 0;
  GRAY_STEPS.forEach(step => {
    if (step > 200) curve[step] = Math.max(curve[step], prev + 0.02);
    curve[step] = Math.min(curve[step], 1);
    prev = curve[step];
  });
  const mode = s.neutralMode || 'primary';
  const hueSource = mode === 'tint' ? s.neutralTint : mode === 'primary' ? s.primary : null;
  const hue = hueSource ? oklabToOklch(labOf(hueSource)).h : 0;
  const tinted = t => {
    const chroma = hueSource ? NEUTRAL_CHROMA * (1 - Math.abs(t - 0.5) * 1.2) : 0;
    return withHue(mixHex(surface, text, t), hue, chroma);
  };
  const ramp = {};
  GRAY_STEPS.forEach(step => { ramp[`--color-gray-${step}`] = tinted(curve[step]); });
  // The border keeps its own 2:1 position; gray-400 itself is pushed to 3:1 for icons.
  const border = tinted(Math.min(Math.max(borderAt, curve[300] + 0.02), curve[400]));
  // gray-100 is a real step off the surface so zebra rows, header rows and button fills show.
  const fill = mixHex(surface, text, pushTo(surface, text, 0.02, FILL_MIN, null, 0.005));

  // Primary family: one hue, blended toward the surface.
  const primaryLight = flooredBlend(s.primary, surface, 0.92, PRIMARY_LIGHT_MIN, 0.6);
  const primaryDark = dark ? mixHex(s.primary, surface, 0.3) : shiftLightness(s.primary, -0.1);
  const barBg = s.barBg || s.primary;
  // Primary as text (titles, icons, links, active items): AA on the surface and on primary-light,
  // and on the page only when that still holds on the surface. Nearly all of it sits on the
  // surface, and a surface and page far apart in lightness can leave no color that reads on both.
  const onSurface = readableShade(readableShade(s.primary, surface), primaryLight);
  const onPage = readableShade(onSurface, page);
  const primaryText = ratioHex(onPage, surface) >= TEXT_MIN ? onPage : onSurface;

  // Status: backgrounds floored against the surface, text shaded until it reads on them.
  const status = {};
  ['error', 'success', 'warning'].forEach(name => {
    const light = flooredBlend(s[name], surface, 0.85, STATUS_LIGHT_MIN);
    status[`--color-${name}-light`] = light;
    // Readable on its own -light fill and as plain text on the surface.
    status[`--color-${name}-text`] = readableShade(readableShade(s[name], light), surface);
  });

  const tokens = {
    '--color-primary': s.primary,
    '--color-primary-dark': primaryDark,
    '--color-primary-light': primaryLight,
    '--color-primary-tint': primaryLight,
    '--color-primary-text': primaryText,
    '--color-bg-page': page,
    '--color-bg-white': surface,
    '--color-gray-50': page,
    '--color-gray-100': fill,
    ...ramp,
    '--color-gray-900': text,
    '--color-error': s.error,
    '--color-error-hover': mixHex(s.error, surface, 0.78),
    '--color-success': s.success,
    '--color-warning': s.warning,
    ...status,
    '--color-dirty-bg': flooredBlend(s.warning, surface, 0.88, STATUS_LIGHT_MIN),
    '--color-dirty-border': s.warning,
    '--color-bar-bg': barBg,
    '--color-bar-text': onColor(barBg),
    '--color-on-primary': onColor(s.primary),
    '--color-on-error': onColor(s.error),
    '--color-on-success': onColor(s.success),
    '--color-on-warning': onColor(s.warning),
    '--shadow-sm': dark ? '0 1px 3px rgba(0,0,0,0.7)' : '0 1px 3px rgba(0,0,0,0.1)',
    '--shadow-md': dark ? '0 4px 6px rgba(0,0,0,0.8)' : '0 4px 6px rgba(0,0,0,0.15)',
    '--color-border': border,
    '--color-border-subtle': ramp['--color-gray-300'],
    '--border-width': s.borderWidth,
    '--focus-ring-width': s.focusRingWidth,
    '--radius-sm': s.radius,
    '--icon-stroke': String(s.iconStroke),
  };

  (s.overrides || []).forEach(name => {
    if (previousTokens && previousTokens[name] != null && name in tokens) tokens[name] = previousTokens[name];
  });

  // Canonical order, nothing extra
  const ordered = {};
  TOKEN_NAMES.forEach(n => { ordered[n] = tokens[n]; });
  return ordered;
}

/** Harmony chips feed the accent seeds: chip 1 → primary, chip 2 → bar, chip 3 → neutral tint. */
export function harmonyToSeeds(hexes, seeds) {
  const next = { ...(seeds || DEFAULT_SEEDS) };
  const [c1, c2, c3] = hexes || [];
  if (c1) next.primary = c1;
  if (c2) next.barBg = c2;
  if (c3) next.neutralTint = c3;
  return next;
}

/* ── Contrast (WCAG AA, warn only) ─────────────────────────── */

export const AA_RATIO = 4.5;

export const CONTRAST_PAIRS = [
  { label: 'Text on backdrop',    fg: '--color-gray-900',  bg: '--color-bg-page' },
  { label: 'Text on panels',      fg: '--color-gray-900',  bg: '--color-bg-white' },
  { label: 'Muted on panels',     fg: '--color-gray-500',  bg: '--color-bg-white' },
  { label: 'Muted on backdrop',   fg: '--color-gray-500',  bg: '--color-bg-page' },
  { label: 'Primary text on panels', fg: '--color-primary-text', bg: '--color-bg-white' },
  { label: 'Primary text on selection', fg: '--color-primary-text', bg: '--color-primary-light' },
  { label: 'Text on warning fill', fg: '--color-on-warning', bg: '--color-warning' },
  { label: 'Header text on header',fg: '--color-bar-text',  bg: '--color-bar-bg' },
  { label: 'Text on primary fill', fg: '--color-on-primary', bg: '--color-primary' },
  { label: 'Error text on panels', fg: '--color-error-text', bg: '--color-bg-white' },
  { label: 'Text on error fill',  fg: '--color-on-error',  bg: '--color-error' },
  { label: 'Text on success fill', fg: '--color-on-success', bg: '--color-success' },
  { label: 'Success text on panels', fg: '--color-success-text', bg: '--color-bg-white' },
  { label: 'Error text on error tint',     fg: '--color-error-text',   bg: '--color-error-light' },
  { label: 'Success text on success tint', fg: '--color-success-text', bg: '--color-success-light' },
  { label: 'Warning text on warning tint', fg: '--color-warning-text', bg: '--color-warning-light' },
];

/** [{ label, fg, bg, ratio, pass }] — fg/bg are the resolved hex values. */
export function checkContrast(tokens) {
  const t = tokens || {};
  return CONTRAST_PAIRS.map(p => {
    const fg = t[p.fg], bg = t[p.bg];
    const ok = fg && bg && parseColor(fg) && parseColor(bg);
    const ratio = ok ? Math.round(ratioHex(fg, bg) * 100) / 100 : 0;
    return { label: p.label, fg, bg, ratio, pass: ok ? ratio >= AA_RATIO : false };
  });
}
